/**
 * Hooks for fetching and managing treatment cycles
 */

import { useMemo } from "react";
import { cycleKeys, cycleMutationInvalidationKeys } from "../cycle-cache-keys";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { useTranslations } from "@src/packages/i18n";
import type { paths } from "@src/packages/openapi/schema";
import { toLocalDateString, getTodayString } from "@src/packages/utils/date";
import { mergeOutcomeNotes } from "../presenters/outcomeNotes";
import {
  buildPatientOutcomeWrite,
  CycleOutcomeWriteError,
  outcomeWriteWasNotApplied,
} from "../presenters/patientOutcomeWrite";
import {
  ACTIVE_CYCLE_REVIEW_MOCK,
  DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE,
  DEV_ACTIVE_CYCLE_OVERRIDE,
  DEV_FORCE_NO_ACTIVE_CYCLE,
  DEV_FORCE_NO_PAST_CYCLES,
  MOCK_CLOSED_CYCLE,
  MOCK_CYCLE_ID,
  MOCK_CYCLE_PHASES,
} from "../dev/devActiveCycleOverride";

// ---------------------------------------------------------------------------
// Schema-derived types
// ---------------------------------------------------------------------------

type CyclesResponse =
  paths["/{tenantId}/patients/{patientId}/cycles"]["get"]["responses"]["200"]["content"]["application/json"];

type PhasesResponse =
  paths["/{tenantId}/cycles/{id}/phases"]["get"]["responses"]["200"]["content"]["application/json"];

type CyclePatchBody = NonNullable<
  paths["/{tenantId}/cycles/{id}"]["patch"]["requestBody"]
>["content"]["application/json"];

type OutcomeAcknowledgementBody = NonNullable<
  paths["/{tenantId}/cycles/{cycleId}/outcome/acknowledge"]["post"]["requestBody"]
>["content"]["application/json"];

type CycleOutcomeCreateBody = NonNullable<
  paths["/{tenantId}/cycles/{cycleId}/outcomes"]["post"]["requestBody"]
>["content"]["application/json"];

type CycleProcedureEventsResponse =
  paths["/{tenantId}/cycles/{cycleId}/procedure-events"]["get"]["responses"]["200"]["content"]["application/json"];

type CycleProcedureEventCreateBody = NonNullable<
  paths["/{tenantId}/cycles/{cycleId}/procedure-events"]["post"]["requestBody"]
>["content"]["application/json"];

type CycleCreateBody = NonNullable<
  paths["/{tenantId}/cycles"]["post"]["requestBody"]
>["content"]["application/json"];

// ---------------------------------------------------------------------------
// Exported domain types
// ---------------------------------------------------------------------------

export type Cycle = CyclesResponse["data"][number];
/**
 * A cycle's recorded ending, as the API returns it.
 *
 * Exported so a caller can hand the row to something that has to name it. An
 * outcome body carrying an `id` is applied as a partial column update, so
 * omitted fields are *not* destroyed — see `outcomeWriteFor`'s note for
 * what actually was. What a caller must not do is write without the id.
 */
export type CycleOutcome = NonNullable<Cycle["outcome"]>;
/**
 * The outcome sub-object a cycle PATCH accepts — every field a write can set.
 *
 * `resultStatus`, the provenance fields, `verificationStatus` and
 * `acknowledgedAt` are deliberately absent. They cannot be set through here, and
 * because an id-bearing write updates only the columns it carries, they cannot
 * be lost through here either.
 */
export type CycleOutcomePatch = NonNullable<CyclePatchBody["outcome"]>;
export type CycleType = Cycle["cycleType"];
export type CycleStatus = Cycle["status"];
export type CycleOutcomeResultStatus = CycleOutcomeCreateBody["resultStatus"];
export type CycleProcedureEvent = CycleProcedureEventsResponse["data"][number];

export type Phase = PhasesResponse["data"][number];
export type PhaseType = Phase["type"];

/**
 * The subset of cycle data needed to resolve the patient-facing active-cycle
 * header. Keeping this contract narrow makes the resolver straightforward to
 * exercise with API-shaped fixtures without duplicating the full cycle row.
 */
export type CycleHeaderCycle = Pick<
  Cycle,
  | "cycleType"
  | "endDate"
  | "outcome"
  | "protocol"
  | "menstruationStartDate"
  | "startDate"
  | "cyclePlanSnapshot"
>;

/**
 * The phase fields used by the active-cycle header resolver. The complete API
 * phase remains the source of truth; this is only the resolver's read shape.
 */
export type CycleHeaderPhase = Pick<
  Phase,
  | "id"
  | "type"
  | "startDate"
  | "endDate"
  | "fertilityMetrics"
  | "triggerShot"
  | "testResults"
>;

export type PatientMedication = NonNullable<
  Phase["patientMedications"]
>[number];

/**
 * Cycle IDs used by deterministic product-route fixtures. These IDs are only
 * resolved to local mock data in development builds; production always uses
 * the API-backed cycle collection.
 */
export const DEV_NAVIGABLE_CYCLE_IDS = new Set([
  MOCK_CYCLE_ID,
  "dev-cycle-bridge",
  "dev-cycle-ivf-2026-03",
  "dev-cycle-thaw-icsi-2026-05",
  "dev-cycle-fet-2026-07",
]);

export function isDevNavigableCycleId(
  cycleId: string | null | undefined,
): boolean {
  return __DEV__ && !!cycleId && DEV_NAVIGABLE_CYCLE_IDS.has(cycleId);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Human-readable labels for cycle types (English fallback for non-React callers).
 */
export const CYCLE_TYPE_LABELS: Partial<Record<CycleType, string>> = {
  ivf: "IVF cycle",
  iui: "IUI cycle",
  fet: "FET cycle",
  icsi: "IVF + ICSI cycle",
  oi: "Ovulation induction",
  egg_freezing: "Egg freezing",
  embryo_freezing: "Embryo freezing",
  sperm_freezing: "Sperm freezing",
};

export const CYCLE_TYPE_LABEL_KEYS: Partial<Record<CycleType, string>> = {
  ivf: "care.cycle_type.ivf",
  iui: "care.cycle_type.iui",
  fet: "care.cycle_type.fet",
  icsi: "care.cycle_type.icsi",
  oi: "care.cycle_type.oi",
  egg_freezing: "care.cycle_type.egg_freezing",
  embryo_freezing: "care.cycle_type.embryo_freezing",
  sperm_freezing: "care.cycle_type.sperm_freezing",
};

/**
 * Human-readable labels for phase types (English fallback for non-React callers).
 */
export const PHASE_TYPE_LABELS: Partial<Record<PhaseType, string>> = {
  pre_treatment: "Pre-Treatment",
  protocol: "Stimulation Phase",
  egg_retrieval: "Egg Retrieval",
  sperm_collection: "Sperm Collection",
  fertilization: "Fertilization",
  insemination: "Insemination",
  embryo_development: "Embryo Development",
  transfer: "Embryo Transfer",
  luteal_phase: "Waiting Period",
};

const PHASE_TYPE_LABEL_KEYS: Partial<Record<PhaseType, string>> = {
  pre_treatment: "care.phase_type.pre_treatment",
  protocol: "care.phase_type.protocol",
  egg_retrieval: "care.phase_type.egg_retrieval",
  sperm_collection: "care.phase_type.sperm_collection",
  fertilization: "care.phase_type.fertilization",
  insemination: "care.phase_type.insemination",
  embryo_development: "care.phase_type.embryo_development",
  transfer: "care.phase_type.transfer",
  luteal_phase: "care.phase_type.luteal_phase",
};

/**
 * Hook that returns the cycle-type labels resolved for the current locale.
 * Same shape as CYCLE_TYPE_LABELS but translated.
 */
export function useCycleTypeLabels(): Partial<Record<CycleType, string>> {
  const t = useTranslations();
  return useMemo(() => {
    const out: Partial<Record<CycleType, string>> = {};
    for (const [type, key] of Object.entries(CYCLE_TYPE_LABEL_KEYS)) {
      out[type as CycleType] = t(key);
    }
    return out;
  }, [t]);
}

/**
 * Hook that returns the phase-type labels resolved for the current locale.
 * Same shape as PHASE_TYPE_LABELS but translated.
 */
export function usePhaseTypeLabels(): Partial<Record<PhaseType, string>> {
  const t = useTranslations();
  return useMemo(() => {
    const out: Partial<Record<PhaseType, string>> = {};
    for (const [type, key] of Object.entries(PHASE_TYPE_LABEL_KEYS)) {
      out[type as PhaseType] = t(key);
    }
    return out;
  }, [t]);
}

/**
 * Phase order for progress calculation
 */
export const PHASE_ORDER: Partial<Record<CycleType, PhaseType[]>> = {
  ivf: [
    "pre_treatment",
    "protocol",
    "egg_retrieval",
    "fertilization",
    "embryo_development",
    "transfer",
    "luteal_phase",
  ],
  icsi: [
    "pre_treatment",
    "protocol",
    "egg_retrieval",
    "sperm_collection",
    "fertilization",
    "embryo_development",
    "transfer",
    "luteal_phase",
  ],
  iui: [
    "pre_treatment",
    "protocol",
    "sperm_collection",
    "insemination",
    "luteal_phase",
  ],
  fet: ["pre_treatment", "protocol", "transfer", "luteal_phase"],
  oi: ["pre_treatment", "protocol", "luteal_phase"],
  egg_freezing: ["pre_treatment", "protocol", "egg_retrieval"],
  embryo_freezing: [
    "pre_treatment",
    "protocol",
    "egg_retrieval",
    "sperm_collection",
    "fertilization",
    "embryo_development",
  ],
};

/**
 * OI can terminate in timed intercourse or continue through an IUI. The
 * insemination phase is therefore inserted only when the server returned it;
 * the fixed OI order must not make every OI cycle look like an IUI.
 */
export function getPhaseOrderForCycle(
  cycleType: CycleType,
  phases: Pick<Phase, "type">[] = [],
): PhaseType[] {
  const base = PHASE_ORDER[cycleType] ?? [];
  if (
    cycleType !== "oi" ||
    !phases.some((phase) => phase.type === "insemination")
  ) {
    return base;
  }

  const lutealIndex = base.indexOf("luteal_phase");
  if (lutealIndex < 0) return [...base, "sperm_collection", "insemination"];
  return [
    ...base.slice(0, lutealIndex),
    "sperm_collection",
    "insemination",
    ...base.slice(lutealIndex),
  ];
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Hook to get all cycles for the current patient
 */
export function useCycles(opts?: { patientId?: string | null }) {
  const { client, tenantId, patientId } = useApiClient();
  const scopedPatientId = opts?.patientId ?? patientId;

  const query = useQuery({
    queryKey: cycleKeys.list(tenantId ?? "", scopedPatientId ?? ""),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/cycles",
        {
          headers: { "x-api-version": "2026-06-17" },
          params: {
            path: {
              tenantId,
              patientId: scopedPatientId!,
            },
          },
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to load cycles",
          "useCycles",
          response?.status,
        );
      }
    },
    enabled: !!scopedPatientId && !!tenantId,
  });

  if (DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE) {
    return {
      ...query,
      data: [MOCK_CLOSED_CYCLE],
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  if (DEV_FORCE_NO_PAST_CYCLES) {
    return {
      ...query,
      data: [],
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  return query;
}

/**
 * Hook to get the active (open) cycle for the current patient — or
 * a specific patient when `opts.patientId` is supplied (used by
 * partner-perspective hooks).
 */
export function useActiveCycle(opts?: { patientId?: string | null }) {
  const { client, tenantId, patientId: currentPatientId } = useApiClient();
  const patientId = opts?.patientId ?? currentPatientId;

  const query = useQuery({
    queryKey: cycleKeys.active(tenantId ?? "", patientId ?? ""),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/active-cycle",
        {
          headers: { "x-api-version": "2026-06-17" },
          params: {
            path: {
              tenantId,
              patientId: patientId!,
            },
          },
        },
      );

      if (data) {
        return data.data ?? null;
      }

      // No data and no error means no active cycle
      if (!error) {
        return null;
      }

      // Throw on actual errors so React Query retries and preserves cached data
      throwApiError(
        error,
        "Failed to fetch active cycle",
        "useActiveCycle",
        response?.status,
      );
    },
    enabled: !!patientId && !!tenantId,
    staleTime: 30_000,
  });

  if (DEV_ACTIVE_CYCLE_OVERRIDE) {
    return {
      ...query,
      data: ACTIVE_CYCLE_REVIEW_MOCK,
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  if (DEV_FORCE_NO_ACTIVE_CYCLE) {
    return {
      ...query,
      data: null,
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  if (DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE) {
    return {
      ...query,
      data: null,
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  return query;
}

/**
 * Hook to get phases for a specific cycle
 */
export function useCyclePhases(cycleId: string | undefined) {
  const { client, tenantId } = useApiClient();

  const query = useQuery({
    queryKey: cycleKeys.phases(tenantId ?? "", cycleId ?? ""),
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/cycles/{id}/phases",
        {
          headers: { "x-api-version": "2026-06-17" },
          params: {
            path: {
              tenantId,
              id: cycleId!,
            },
          },
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to load cycle phases",
          "useCyclePhases",
          response?.status,
        );
      }
    },
    enabled: !!cycleId && !!tenantId,
  });

  if (DEV_ACTIVE_CYCLE_OVERRIDE && cycleId === MOCK_CYCLE_ID) {
    return {
      ...query,
      data: MOCK_CYCLE_PHASES,
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  if (DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE && cycleId === MOCK_CLOSED_CYCLE.id) {
    return {
      ...query,
      data: MOCK_CYCLE_PHASES.map((phase) => ({
        ...phase,
        cycleId,
      })),
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  if (isDevNavigableCycleId(cycleId)) {
    return {
      ...query,
      data: MOCK_CYCLE_PHASES.map((phase) => ({
        ...phase,
        cycleId,
      })),
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  return query;
}

export function useCycleProcedureEvents(cycleId: string | null | undefined) {
  const { client, tenantId } = useApiClient();

  return useQuery({
    queryKey: ["cycle-procedure-events", tenantId ?? "", cycleId ?? ""],
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/cycles/{cycleId}/procedure-events",
        {
          headers: { "x-api-version": "2026-06-17" },
          params: {
            path: {
              tenantId,
              cycleId: cycleId!,
            },
          },
        },
      );

      if (data) return data.data;
      throwApiError(
        error,
        "Failed to load cycle procedure events",
        "useCycleProcedureEvents",
        response?.status,
      );
    },
    enabled: !!cycleId && !!tenantId,
  });
}

function createProcedureEventRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  const randomHex = (length: number) =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join("");
  const variant = (8 + Math.floor(Math.random() * 4)).toString(16);
  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-${variant}${randomHex(3)}-${randomHex(12)}`;
}

export function useCreateCycleProcedureEvent() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      body,
    }: {
      cycleId: string;
      body: Omit<CycleProcedureEventCreateBody, "clientRequestId"> & {
        clientRequestId?: CycleProcedureEventCreateBody["clientRequestId"];
      };
    }) => {
      if (!tenantId) throw new Error("Missing tenantId");

      const { data, error, response } = await client.POST(
        "/{tenantId}/cycles/{cycleId}/procedure-events",
        {
          headers: { "x-api-version": "2026-06-17" },
          params: {
            path: { tenantId, cycleId },
          },
          body: {
            ...body,
            clientRequestId:
              body.clientRequestId ?? createProcedureEventRequestId(),
          },
        },
      );

      if (data) return data.data;
      throwApiError(
        error,
        "Failed to save cycle procedure event",
        "useCreateCycleProcedureEvent",
        response?.status,
      );
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["cycle-procedure-events", tenantId ?? "", variables.cycleId],
      });
      for (const key of cycleMutationInvalidationKeys()) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

/**
 * Hook to get active cycle with phases — optionally for a specific patient
 * (used by partner-perspective auto-pull).
 */
export function useActiveCycleWithPhases(opts?: {
  patientId?: string | null;
  /** Resolve a specific open cycle for deep-link navigation when provided. */
  cycleId?: string | null;
}) {
  const activeCycleQuery = useActiveCycle({ patientId: opts?.patientId });
  const cyclesQuery = useCycles({ patientId: opts?.patientId });
  const requestedCycleId = opts?.cycleId ?? null;
  const cycle = requestedCycleId
    ? (cyclesQuery.data?.find(
        (candidate) => candidate.id === requestedCycleId,
      ) ??
      (isDevNavigableCycleId(requestedCycleId)
        ? ({ ...ACTIVE_CYCLE_REVIEW_MOCK, id: requestedCycleId } as Cycle)
        : null))
    : activeCycleQuery.data;
  const phasesQuery = useCyclePhases(cycle?.id);

  const phases = phasesQuery.data ?? [];

  // Calculate current phase and progress
  let currentPhase: Phase | null = null;
  // One-based position in PHASE_ORDER. This must not be used as an index into
  // `phases`, whose server ordering is independent from PHASE_ORDER.
  let currentPhaseIndex = 0;
  let totalPhases = 0;
  let progress = 0;

  if (cycle) {
    const phaseOrder = getPhaseOrderForCycle(cycle.cycleType, phases);
    totalPhases = phaseOrder.length;

    if (phases.length > 0) {
      const sortedPhases = [...phases].sort((a, b) => {
        const orderA = phaseOrder.indexOf(a.type);
        const orderB = phaseOrder.indexOf(b.type);
        return orderA - orderB;
      });

      // Pick current phase by date window (matches CycleDetailScreen timeline).
      // YYYY-MM-DD ISO strings compare lexicographically.
      const todayStr = getTodayString();
      const isActive = (p: Phase) =>
        !!p.startDate &&
        p.startDate <= todayStr &&
        (!p.endDate || p.endDate >= todayStr);
      const isCompleted = (p: Phase) => !!p.endDate && p.endDate < todayStr;

      const activePhases = sortedPhases.filter(isActive);
      if (activePhases.length > 0) {
        // Latest active in PHASE_ORDER handles overlapping windows.
        currentPhase = activePhases[activePhases.length - 1] ?? null;
      } else {
        const completedPhases = sortedPhases.filter(isCompleted);
        if (completedPhases.length > 0) {
          // Gap day: surface the most recent completed phase.
          currentPhase = completedPhases[completedPhases.length - 1] ?? null;
        }
      }

      if (currentPhase) {
        currentPhaseIndex = phaseOrder.indexOf(currentPhase.type) + 1;
      }
    } else {
      // Preserve the existing initial progress behavior until a phase exists.
      currentPhaseIndex = 1;
    }

    // Calculate progress percentage
    if (totalPhases > 0) {
      progress = Math.round((currentPhaseIndex / totalPhases) * 100);
    }
  }

  return {
    cycle,
    phases,
    currentPhase,
    currentPhaseIndex,
    totalPhases,
    progress,
    isLoading:
      (requestedCycleId ? cyclesQuery.isLoading : activeCycleQuery.isLoading) ||
      phasesQuery.isLoading,
    error:
      (requestedCycleId ? cyclesQuery.error : activeCycleQuery.error) ||
      phasesQuery.error,
  };
}

/**
 * Hook to update a cycle (e.g., close it, set outcome)
 */
export function useUpdateCycle() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      data,
    }: {
      cycleId: string;
      data: CyclePatchBody;
    }) => {
      // Validate cycleId before making API call
      if (!cycleId) {
        console.error(
          "[useUpdateCycle] Cannot update cycle: cycleId is undefined or empty",
        );
        throw new Error("Cannot update cycle: cycleId is required");
      }

      // Validate cycleId is a proper UUID
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(cycleId)) {
        console.error("[useUpdateCycle] Invalid cycle ID format:", cycleId);
        throw new Error(
          `Invalid cycle ID format: "${cycleId}". Expected UUID.`,
        );
      }

      console.log("[useUpdateCycle] PATCH request:", {
        endpoint: "/{tenantId}/cycles/{id}",
        tenantId,
        cycleId,
        body: data,
      });
      const result = await client.PATCH("/{tenantId}/cycles/{id}", {
        headers: { "x-api-version": "2026-06-17" },
        params: {
          path: {
            tenantId,
            id: cycleId,
          },
        },
        body: data,
      });
      const err = result.error;
      if (err) {
        console.error("[useUpdateCycle] API error:", err);
        throwApiError(
          err,
          "Failed to update cycle",
          "useUpdateCycle",
          result.response?.status,
        );
      }
      return result;
    },
    onSuccess: (_result, { cycleId, data }) => {
      if ("outcome" in data) {
        queryClient.invalidateQueries({
          queryKey: cycleKeys.pregnancyStatus(cycleId),
        });
      }
      for (const key of cycleMutationInvalidationKeys()) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      queryClient.invalidateQueries({ queryKey: ["medications"] });
    },
  });
}

export function useAcknowledgePostProcedureOutcome() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      outcomeId,
    }: {
      cycleId: string;
      outcomeId: string;
    }) => {
      const { data, error, response } = await client.POST(
        "/{tenantId}/cycles/{cycleId}/outcome/acknowledge",
        {
          headers: { "x-api-version": "2026-06-17" },
          params: {
            path: { tenantId, cycleId },
          },
          body: { outcomeId } satisfies OutcomeAcknowledgementBody,
        },
      );

      if (data) return data.data;
      throwApiError(
        error,
        "Failed to acknowledge post-procedure outcome",
        "useAcknowledgePostProcedureOutcome",
        response?.status,
      );
    },
    // Awaited so `isPending` stays true until the refetched cycle carries
    // `acknowledgedAt`. Returning nothing here leaves a window in which the
    // arrival carousel has closed but the acknowledgement is not yet readable,
    // and Home briefly re-presents the result as the hero.
    onSuccess: () =>
      Promise.all([
        ...cycleMutationInvalidationKeys().map((key) =>
          queryClient.invalidateQueries({ queryKey: key }),
        ),
      ]),
  });
}

/**
 * Cancellation reason categories surfaced in the discontinue modal.
 * Stored on outcome.cancellationReason; free-text lives in outcome.notes.
 */
export type CancellationReason =
  | "medical_advice"
  | "personal_choice"
  | "side_effects"
  | "protocol_switch"
  | "financial"
  | "trigger_not_administered"
  | "no_retrieval"
  | "other";

/**
 * Translation keys for cancellation reasons. Used by the discontinue picker
 * and the past-cycles display so they stay in sync. Look up via `t(key)`.
 */
export const CANCELLATION_REASON_KEYS: Record<CancellationReason, string> = {
  medical_advice: "care.discontinue.reason_medical_advice",
  personal_choice: "care.discontinue.reason_personal_choice",
  side_effects: "care.discontinue.reason_side_effects",
  protocol_switch: "care.discontinue.reason_protocol_switch",
  financial: "care.discontinue.reason_financial",
  trigger_not_administered: "care.discontinue.reason_trigger_not_administered",
  no_retrieval: "care.discontinue.reason_no_retrieval",
  other: "care.discontinue.reason_other",
};

/**
 * Hook to create a new cycle for the current patient
 */
export function useCreateCycle() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: Omit<CycleCreateBody, "patientId"> & { patientId?: string },
    ) => {
      if (!patientId) {
        throw new Error("Cannot create cycle: patientId is required");
      }
      const result = await client.POST("/{tenantId}/cycles", {
        headers: { "x-api-version": "2026-06-17" },
        params: {
          path: { tenantId },
        },
        body: {
          ...data,
          patientId: data.patientId ?? patientId,
          status: data.status ?? "open",
        },
      });
      const err = result.error;
      if (err) {
        // Normalize through the shared contract so the real status and server
        // message survive. Collapsing this to a fixed string made every
        // failure look identical at the call site, which is why a failing
        // "Start cycle" could only ever report "Something went wrong".
        throwApiError(
          err,
          "Failed to create cycle",
          "useCreateCycle",
          result.response?.status,
        );
      }
      return result;
    },
    onSuccess: () => {
      for (const key of cycleMutationInvalidationKeys()) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

/**
 * Hook to discontinue (close) the active cycle. Writes the structured
 * outcome so closed cycles can be reliably distinguished from cycles that
 * finished as planned (see useCompleteCycle).
 *
 * The cycle/outcome relationship is 1:1, so when an outcome row already
 * exists pass its id via `outcomeId` to UPDATE it; omit to INSERT.
 */
export function useDiscontinueCycle() {
  const updateCycle = useUpdateCycle();

  return useMutation({
    mutationFn: async ({
      cycleId,
      outcomeId,
      cancellationReason,
      notes,
    }: {
      cycleId: string;
      outcomeId?: string | null;
      cancellationReason: CancellationReason;
      notes?: string;
    }) => {
      const today = getTodayString();
      console.log(
        "[useDiscontinueCycle] Closing cycle:",
        cycleId,
        cancellationReason,
        notes,
      );
      return updateCycle.mutateAsync({
        cycleId,
        data: {
          status: "closed",
          endDate: today,
          outcome: {
            ...(outcomeId ? { id: outcomeId } : {}),
            type: "cancelled",
            cancellationReason,
            pregnancyOutcome: null,
            miscarriageOutcome: null,
            biochemicalPregnancyObserved: null,
            dateRecorded: today,
            weekCount: null,
            liveBirthCount: null,
            notes: notes || null,
          },
        },
      });
    },
  });
}

/**
 * Hook to complete (close) a preservation cycle — e.g., egg_freezing
 * reaching storage. Leaves outcome.type null; display layer infers
 * "Completed" from cycle type.
 */
export function useCompleteCycle() {
  const updateCycle = useUpdateCycle();

  return useMutation({
    mutationFn: async ({
      cycleId,
      notes,
      outcomeId,
      existingNotes,
      existingDateRecorded,
    }: {
      cycleId: string;
      notes?: string;
      outcomeId?: string;
      /**
       * Anything already stored on the outcome. This write sets `notes`, and a
       * column it sets is a column it overwrites — so a preservation cycle that
       * already carried notes (a medical-history import, an earlier recorded
       * value) lost them when this closed it. Fields this write does not carry
       * are left alone, which is why only `notes` and `dateRecorded` need
       * passing back.
       */
      existingNotes?: string | null;
      existingDateRecorded?: string | null;
    }) => {
      const today = toLocalDateString(new Date());
      const mergedNotes = mergeOutcomeNotes({
        existing: existingNotes,
        added: notes,
      });

      return updateCycle.mutateAsync({
        cycleId,
        data: {
          status: "closed",
          endDate: today,
          outcome: {
            ...(outcomeId ? { id: outcomeId } : {}),
            type: null,
            // Keep the date already recorded against the outcome; only a first
            // record is dated today.
            dateRecorded: existingDateRecorded ?? today,
            notes: mergedNotes,
          },
        },
      });
    },
  });
}

/**
 * Pregnancy outcome subtype options for transfer-bearing cycles.
 */
export type PregnancyOutcomeKind = "live_birth" | "multiple_live_birth";

export type MiscarriageOutcomeKind =
  | "complete_miscarriage"
  | "incomplete_miscarriage"
  | "missed_miscarriage"
  | "still_birth"
  | "recurrent_miscarriage"
  | "chemical_pregnancy"
  | "ectopic_pregnancy"
  | "anembryonic_pregnancy";

export type BetaResultType = "pregnancy" | "no_pregnancy" | "miscarriage";

/**
 * Hook to record the beta hCG result on a transfer-bearing cycle. Patient
 * results remain open while unverified, including determinate positive and
 * negative values; only an explicitly verified determinate result may close
 * the cycle. The legacy branch keeps the existing cycle/outcome update for
 * callers that do not provide the new resultStatus field. The legacy path also
 * preserves existing note/date values when a repeat result replaces an outcome
 * sub-object.
 */
export function useRecordCycleOutcome() {
  const updateCycle = useUpdateCycle();
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      outcomeId,
      type,
      resultStatus,
      sourceKind,
      sourceRecordId,
      sourceDocumentId,
      clientRequestId,
      verificationStatus,
      procedureId,
      procedureType,
      procedureModality,
      procedureDate,
      procedureTime,
      pregnancyOutcome,
      miscarriageOutcome,
      biochemicalPregnancyObserved,
      weekCount,
      liveBirthCount,
      notes,
      existingNotes,
      existingDateRecorded,
      recordedOn,
    }: {
      cycleId: string;
      outcomeId?: string | null;
      type: BetaResultType | null;
      resultStatus?: CycleOutcomeResultStatus;
      sourceKind?: CycleOutcomeCreateBody["sourceKind"];
      sourceRecordId?: CycleOutcomeCreateBody["sourceRecordId"];
      sourceDocumentId?: CycleOutcomeCreateBody["sourceDocumentId"];
      /** Stable across retries so a lost response cannot create a duplicate. */
      clientRequestId?: CycleOutcomeCreateBody["clientRequestId"];
      verificationStatus?: CycleOutcomeCreateBody["verificationStatus"];
      procedureId?: string | null;
      procedureType?: CycleOutcomeCreateBody["procedureType"];
      procedureModality?: CycleOutcomeCreateBody["procedureModality"];
      procedureDate?: string | null;
      procedureTime?: string | null;
      pregnancyOutcome?: PregnancyOutcomeKind | null;
      miscarriageOutcome?: MiscarriageOutcomeKind | null;
      biochemicalPregnancyObserved?: boolean | null;
      /** How far along, and how many babies — see `PatientOutcomeWriteInput`. */
      weekCount?: number | null;
      liveBirthCount?: number | null;
      /**
       * The date this recording is *about*, defaulting to today.
       *
       * A repeat beta is a new result, so today is right for it. An update that
       * only adds detail to a result already recorded is not — leaving it to
       * default moved the date the app shows as "recorded on" to the day she
       * edited, which read as the result having arrived then. Such a caller passes
       * the date already stored.
       */
      recordedOn?: string | null;
      notes?: string;
      /**
       * What the outcome already stores. Recording is reachable more than once —
       * "Update result" exists precisely so a borderline or first positive beta
       * can be followed by a repeat — and a correction replaces the whole record,
       * so without these the earlier value and the date it came in are destroyed.
       * The typed test-result record is the numeric source; the
       * note remains a readable ledger for the outcome.
       */
      existingNotes?: string | null;
      existingDateRecorded?: string | null;
    }) => {
      const today = toLocalDateString(new Date());

      if (resultStatus) {
        if (!tenantId) throw new Error("Missing tenantId");

        const write = buildPatientOutcomeWrite({
          outcomeId,
          type,
          resultStatus,
          sourceKind,
          sourceRecordId,
          sourceDocumentId,
          clientRequestId,
          verificationStatus,
          procedureId,
          procedureType,
          procedureModality,
          procedureDate,
          procedureTime,
          pregnancyOutcome,
          miscarriageOutcome,
          biochemicalPregnancyObserved,
          weekCount,
          liveBirthCount,
          notes,
          existingNotes,
          recordedOn: recordedOn ?? today,
        });

        // A repeat recording supersedes the record already on the cycle instead
        // of adding a second current one. Two current records make the API's
        // singular `outcome` view resolve to null, which would put the patient
        // back on the two-week-wait screen with the result they just entered
        // invisible.
        const { data, error, response } =
          write.kind === "correct"
            ? await client.POST(
                "/{tenantId}/cycles/{cycleId}/outcomes/{outcomeId}/correct",
                {
                  headers: { "x-api-version": "2026-06-17" },
                  params: {
                    path: { tenantId, cycleId, outcomeId: write.outcomeId },
                  },
                  body: write.body,
                },
              )
            : await client.POST("/{tenantId}/cycles/{cycleId}/outcomes", {
                headers: { "x-api-version": "2026-06-17" },
                params: {
                  path: { tenantId, cycleId },
                },
                body: write.body,
              });

        if (error) {
          throw new CycleOutcomeWriteError(
            typeof error === "string"
              ? error
              : error.message || "Failed to record cycle outcome",
            response.status,
            write.kind,
          );
        }
        if (!data) throw new Error("Cycle outcome was not returned");

        /**
         * A replayed request id can be answered with the record already stored
         * rather than the one just sent, and reported as a success — see
         * `outcomeWriteWasNotApplied`. Reported here as the conflict it actually
         * is, so the screen offers the recovery it already has for a real 409
         * instead of telling the patient a value landed when it did not.
         */
        if (
          clientRequestId &&
          outcomeWriteWasNotApplied(write.body, data.data)
        ) {
          throw new CycleOutcomeWriteError(
            "The stored outcome does not carry this write",
            409,
            write.kind,
          );
        }

        // Patient-entered results are unverified even when a threshold-derived
        // status is determinate. Keep the cycle open until a clinic/provider
        // workflow explicitly verifies the result.
        if (
          verificationStatus === "verified" &&
          (resultStatus === "positive" ||
            resultStatus === "negative" ||
            resultStatus === "adverse")
        ) {
          await updateCycle.mutateAsync({
            cycleId,
            data: { status: "closed", endDate: today },
          });
        }

        return data.data;
      }

      return updateCycle.mutateAsync({
        cycleId,
        data: {
          outcome: {
            ...(outcomeId ? { id: outcomeId } : {}),
            type,
            cancellationReason: null,
            pregnancyOutcome: pregnancyOutcome ?? null,
            miscarriageOutcome: miscarriageOutcome ?? null,
            biochemicalPregnancyObserved: biochemicalPregnancyObserved ?? null,
            // The first recording dates the result; a repeat appends its value
            // rather than restamping when it came in.
            dateRecorded: existingDateRecorded ?? today,
            weekCount: null,
            liveBirthCount: null,
            notes: mergeOutcomeNotes({ existing: existingNotes, added: notes }),
          },
        },
      });
    },
    onSuccess: (_result, { cycleId }) => {
      queryClient.invalidateQueries({
        queryKey: cycleKeys.pregnancyStatus(cycleId),
      });
      for (const key of cycleMutationInvalidationKeys()) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: (error, { cycleId }) => {
      // A conflict means the server already holds a record this attempt did not
      // know about — the response to an earlier attempt was lost, not the write.
      // Refreshing the cycle is what makes the retry recoverable: the next
      // attempt sees that record and corrects it instead of failing again.
      if (error instanceof CycleOutcomeWriteError && error.isConflict) {
        queryClient.invalidateQueries({
          queryKey: cycleKeys.pregnancyStatus(cycleId),
        });
        queryClient.invalidateQueries({ queryKey: ["cycles"] });
        queryClient.invalidateQueries({ queryKey: ["active-cycle"] });
      }
    },
  });
}
